import React, { useMemo, useState } from 'react';
import PageHelp from '../components/common/PageHelp';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { getAuditLogs } from '../services/auditService';
import { AuditLog } from '../types/Audit';

const ENTITY_OPTIONS = [
  'user',
  'driver',
  'ride',
  'payment',
  'wallet',
  'zones',
  'staff',
  'subscription-plans',
  'retention-program',
  'notifications',
  'ride-option',
  'app-config',
  'marketing-item',
  'tabbed-section',
  'support-ticket',
];

const methodColor = (method: string | null): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  switch (method) {
    case 'POST':
      return 'success';
    case 'PATCH':
    case 'PUT':
      return 'info';
    case 'DELETE':
      return 'error';
    default:
      return 'default';
  }
};

const JsonBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
  if (value === null || value === undefined) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          mt: 0.5,
          p: 1.5,
          bgcolor: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 1.5,
          fontSize: 12,
          overflowX: 'auto',
          maxHeight: 280,
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    </Box>
  );
};

const AuditLogList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [entity, setEntity] = useState('');
  const [success, setSuccess] = useState<'' | 'true' | 'false'>('');
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isRefetching } =
    useInfiniteQuery({
      queryKey: ['audit-logs', { search, entity, success }],
      initialPageParam: undefined as number | undefined,
      queryFn: ({ pageParam }) =>
        getAuditLogs({
          search: search || undefined,
          entity: entity || undefined,
          success: success === '' ? undefined : success === 'true',
          cursor: pageParam,
          limit: 25,
        }),
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    });

  const rows = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const applySearch = () => setSearch(searchInput.trim());

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Audit Log
        </Typography>
        <PageHelp k="audit" />
        <Box flex={1} />
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()} disabled={isRefetching}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every state-changing admin action is recorded here — who did what, when, and the result.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            label="Search (actor, action, entity, path)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            sx={{ minWidth: 280 }}
          />
          <TextField
            size="small"
            select
            label="Entity"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {ENTITY_OPTIONS.map((e) => (
              <MenuItem key={e} value={e}>
                {e}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Result"
            value={success}
            onChange={(e) => setSuccess(e.target.value as '' | 'true' | 'false')}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Success</MenuItem>
            <MenuItem value="false">Failed</MenuItem>
          </TextField>
          <Button variant="contained" onClick={applySearch}>
            Apply
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Entity</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Path</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {isError && !isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'error.main' }}>
                  Failed to load audit logs.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No audit entries match these filters.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => setSelected(row)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {format(new Date(row.createdAt), 'dd MMM, HH:mm:ss')}
                </TableCell>
                <TableCell>
                  <Box sx={{ fontWeight: 600 }}>{row.actorUsername ?? '—'}</Box>
                  <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{row.actorRole ?? ''}</Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.action}</TableCell>
                <TableCell>{row.entity}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.method ?? '—'} color={methodColor(row.method)} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.statusCode ?? '—'}
                    color={row.success ? 'success' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'text.secondary' }}>
                  {row.path}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasNextPage && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} variant="outlined">
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!selected} onClose={() => setSelected(null)}>
        <Box sx={{ width: { xs: '100vw', sm: 460 }, p: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
              Audit Entry #{selected?.id}
            </Typography>
            <IconButton onClick={() => setSelected(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {selected && (
            <>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Row label="When" value={format(new Date(selected.createdAt), 'dd MMM yyyy, HH:mm:ss')} />
                <Row label="Actor" value={`${selected.actorUsername ?? '—'} (${selected.actorRole ?? '—'})`} />
                <Row label="Action" value={selected.action} mono />
                <Row label="Entity" value={`${selected.entity}${selected.entityId ? ` · ${selected.entityId}` : ''}`} />
                <Row label="Request" value={`${selected.method ?? ''} ${selected.path ?? ''}`} mono />
                <Row label="Result" value={`${selected.statusCode ?? '—'} · ${selected.success ? 'Success' : 'Failed'}`} />
                <Row label="IP" value={selected.ip ?? '—'} />
              </Stack>
              <JsonBlock label="BEFORE" value={selected.before} />
              <JsonBlock label="AFTER" value={selected.after} />
              <JsonBlock label="REQUEST BODY" value={selected.requestBody} />
              <JsonBlock label="RESPONSE" value={selected.responseSummary} />
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="body2" sx={{ minWidth: 80, color: 'text.secondary', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

export default AuditLogList;
